import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../../hooks/usePermissions';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  AlertTitle,
  Grid
} from '@mui/material';
import {
  FileDownload as FileDownloadIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import inventoryReportService, {
  InventoryReportItem,
  InventoryReportFilters,
  Warehouse,
  Category as ReportCategory
} from '../../../services/inventoryReportService';
import InventoryReportFilter from './InventoryReportFilter';
import InventoryReportTable from './InventoryReportTable';
import * as XLSX from 'xlsx';

// تعريف الواجهات
interface Category {
  _id: string;
  name: string;
  description?: string;
}

interface FilterData {
  barcode: string;
  productName: string;
  category: string;
  warehouse: string;
}

interface InventoryItem {
  id: string;
  barcode: string;
  productName: string;
  variant: string;
  category: string;
  allCategories: string[];
  categoryCount: number;
  warehouses: {
    [key: string]: number;
  };
}

const InventoryReport: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  
  // الصلاحيات المطلوبة لتقرير المخزون (بناءً على inventoryReportRoutes.js)
  const hasInventoryReportPermission = hasPermission('reports', 'inventory', 'view_page');
  const hasInventoryExportPermission = hasPermission('reports', 'inventory', 'export_excel'); // للتصدير

  // حالات المكون
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingTotals, setLoadingTotals] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [inventoryData, setInventoryData] = useState<InventoryItem[]>([]);
  const [filteredData, setFilteredData] = useState<InventoryItem[]>([]);
  const [warehouses, setWarehouses] = useState<string[]>([]);
  const [categoriesList, setCategoriesList] = useState<{ id: string; name: string }[]>([]);
  const [filterValues, setFilterValues] = useState<FilterData>({
    barcode: '',
    productName: '',
    category: '',
    warehouse: ''
  });

  // حالات التصفح (server-side pagination)
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(50);
  const [totalItems, setTotalItems] = useState<number>(0);
  
  // حالات الإجماليات الكلية من السيرفر
  const [warehouseTotals, setWarehouseTotals] = useState<{ [key: string]: number }>({});
  const [grandTotal, setGrandTotal] = useState<number>(0);

  // معرّفات الطلبات لمنع حالة التسابق (كتابة نتيجة طلب قديم فوق نتيجة طلب أحدث)
  const mainRequestIdRef = useRef<number>(0);
  const totalsRequestIdRef = useRef<number>(0);

  // تم إزالة التوجيه التلقائي - المستخدم يبقى في الصفحة حتى لو لم تكن لديه صلاحيات

  // تحميل البيانات الأولية
  useEffect(() => {
    const initializeData = async () => {
      try {
        let catList: { id: string; name: string }[] = [];
        let warehouseNames: string[] = [];

        // جلب التصنيفات من خدمة المخزون
        try {
          console.log('🔄 بدء جلب التصنيفات من API...');
          const categoriesResponse = await inventoryReportService.getCategories();
          console.log('📥 استجابة API التصنيفات:', categoriesResponse);
          console.log('📥 نوع الاستجابة:', typeof categoriesResponse);
          console.log('📥 success:', categoriesResponse?.success);
          console.log('📥 data:', categoriesResponse?.data);
          
          if (categoriesResponse && categoriesResponse.success && categoriesResponse.data && Array.isArray(categoriesResponse.data)) {
            catList = categoriesResponse.data.map(cat => ({
              id: cat._id,
              name: cat.name
            }));
            setCategoriesList(catList);
            console.log('✅ تم جلب التصنيفات من API المخزون:', catList);
            console.log('📊 عدد التصنيفات:', catList.length);
          } else if (categoriesResponse && categoriesResponse.data && Array.isArray(categoriesResponse.data)) {
            // في حالة عدم وجود success flag
            catList = categoriesResponse.data.map(cat => ({
              id: cat._id,
              name: cat.name
            }));
            setCategoriesList(catList);
            console.log('✅ تم جلب التصنيفات (بدون success flag):', catList);
          } else {
            console.warn('⚠️ استجابة API التصنيفات غير صحيحة:', categoriesResponse);
            setCategoriesList([]);
          }
        } catch (categoriesError) {
          console.error('❌ فشل جلب التصنيفات من API المخزون:', categoriesError);
          console.log('🔧 يحتاج إنشاء /api/inventory-report/categories في الخادم');
          setCategoriesList([]);
        }

        // جلب المستودعات من خدمة المخزون
        try {
          const warehousesResponse = await inventoryReportService.getWarehouses();
          if (warehousesResponse.data && Array.isArray(warehousesResponse.data)) {
            warehouseNames = warehousesResponse.data.map(wh => wh.name);
          }
          setWarehouses(warehouseNames);
        } catch (whError) {
          console.warn('فشل جلب المستودعات من API المخزون:', whError);
          console.log('يحتاج إنشاء /api/inventory-report/warehouses في الخادم');
          warehouseNames = ['المستودع الرئيسي', 'مستودع فرعي'];
          setWarehouses(warehouseNames);
        }

        // جلب بيانات المخزون مع التصنيفات المُحملة
        await loadInventoryData(warehouseNames, catList);

      } catch (error) {
        console.error('خطأ في تحميل البيانات:', error);
        setError('حدث خطأ أثناء تحميل البيانات.');
        setInventoryData([]);
        setFilteredData([]);
        setWarehouses([]);
        setCategoriesList([]);
        setLoading(false);
      }
    };
    
    initializeData();
  }, []);

  // تحميل بيانات المخزون عند تغيير الصفحة أو عدد الصفوف أو الفلاتر
  useEffect(() => {
    if (warehouses.length > 0 && categoriesList.length >= 0) {
      loadInventoryData(warehouses, categoriesList);
    }
  }, [page, rowsPerPage, filterValues]);

  // دالة تحميل بيانات المخزون باستخدام خدمة المخزون المخصصة
  const loadInventoryData = async (warehouseNames: string[], catList: { id: string; name: string }[]) => {
    // رقم فريد لهذا الطلب لتمييزه عن أي طلب أقدم/أحدث
    const requestId = ++mainRequestIdRef.current;
    try {
      setLoading(true);
      
      // بناء الفلاتر للإرسال للسيرفر
      const filters: any = {
        page: page + 1, // API يستخدم 1-based indexing
        limit: rowsPerPage,
        sortBy: 'productName',
        sortOrder: 'asc',
        includeTotals: false // لا نريد الإجماليات في الطلب الأول (لتسريع التحميل)
      };

      // إضافة الفلاتر إذا كانت موجودة
      if (filterValues.barcode) {
        filters.search = filterValues.barcode;
      }
      if (filterValues.productName) {
        filters.search = filterValues.productName;
      }
      if (filterValues.category) {
        filters.category = filterValues.category;
      }
      if (filterValues.warehouse) {
        filters.warehouse = filterValues.warehouse;
      }
      
      // 1️⃣ تحميل البيانات الأساسية أولاً (سريع)
      console.log('🚀 بدء تحميل البيانات الأساسية...');
      const response = await inventoryReportService.getInventoryReport(filters);

      // إذا بدأ طلب أحدث أثناء انتظار هذا الطلب، نتجاهل هذه النتيجة القديمة
      if (requestId !== mainRequestIdRef.current) {
        console.log('⏭️ تجاهل بيانات أساسية قديمة (طلب أحدث قيد التنفيذ)');
        return;
      }
      
      if (!response.data || !Array.isArray(response.data)) {
        setInventoryData([]);
        setFilteredData([]);
        setTotalItems(0);
        setLoading(false);
        return;
      }

      // تحديث إجمالي عدد العناصر
      if (response.pagination) {
        setTotalItems(response.pagination.total);
      }

      // معالجة بيانات المخزون
      const inventoryItems: InventoryItem[] = [];

      console.log('✅ تم تحميل البيانات الأساسية:', {
        totalItems: response.data.length,
        firstItem: response.data[0],
        pagination: response.pagination
      });

      for (const reportItem of response.data) {
        try {
          // استخراج الباركود
          const defaultBarcode = reportItem.barcode || 'غير متوفر';

          // إنشاء اسم المتغير من الخصائص
          const variantName = Object.values(reportItem.variantAttributes || {}).join(' - ') || 'غير محدد';

          // استخراج التصنيفات (أسماء حقيقية من API)
          let categoryName = 'غير مصنف';
          let allCategories: string[] = [];
          let categoryCount = 0;
          
          if (reportItem.categories && reportItem.categories.length > 0) {
            allCategories = reportItem.categories.map(cat => cat.name || 'غير مسمى');
            categoryCount = allCategories.length;
            
            if (categoryCount === 1) {
              categoryName = allCategories[0];
            } else {
              categoryName = `${allCategories[0]} +${categoryCount - 1}`;
            }
          }

          // استخراج كميات المستودعات (بيانات حقيقية من API)
          const warehouseQuantities: {[key: string]: number} = {};
          
          // تهيئة جميع المستودعات بقيمة 0
          warehouseNames.forEach(name => {
            warehouseQuantities[name] = 0;
          });

          // ملء كميات المستودعات من البيانات
          if (reportItem.warehouseInventories && Array.isArray(reportItem.warehouseInventories)) {
            reportItem.warehouseInventories.forEach(whInventory => {
              const warehouseName = whInventory.warehouse?.name || whInventory.warehouseName;
              if (warehouseName) {
                warehouseQuantities[warehouseName] = whInventory.quantity || 0;
              }
            });
          }

          inventoryItems.push({
            id: reportItem._id || `item-${inventoryItems.length + 1}`,
            barcode: defaultBarcode,
            productName: reportItem.productName || reportItem.product?.name || 'غير محدد',
            variant: variantName,
            category: categoryName,
            allCategories: allCategories,
            categoryCount: categoryCount,
            warehouses: warehouseQuantities
          });

        } catch (itemError) {
          console.error(`خطأ في معالجة عنصر المخزون: ${reportItem._id}`, itemError);
        }
      }

      console.log('📊 عدد العناصر المحملة:', inventoryItems.length);
      console.log('📄 الصفحة الحالية:', page + 1);
      console.log('📏 عدد الصفوف المطلوب:', rowsPerPage);
      console.log('🔢 إجمالي العناصر:', response.pagination?.total);

      setInventoryData(inventoryItems);
      setFilteredData(inventoryItems); // نبقيها للتوافق مع الكود القديم
      setLoading(false); // ✅ الصفحة جاهزة للعرض!

      // 2️⃣ تحميل الإجماليات في الخلفية (بطيء - لا يؤثر على عرض الصفحة)
      console.log('🔄 بدء تحميل الإجماليات في الخلفية...');
      const totalsRequestId = ++totalsRequestIdRef.current;
      setLoadingTotals(true);
      
      try {
        const totalsFilters: any = {};
        if (filterValues.barcode) totalsFilters.search = filterValues.barcode;
        if (filterValues.productName) totalsFilters.search = filterValues.productName;
        if (filterValues.category) totalsFilters.category = filterValues.category;
        if (filterValues.warehouse) totalsFilters.warehouse = filterValues.warehouse;

        const totalsResponse = await inventoryReportService.getTotals(totalsFilters);

        // لا نكتب النتيجة إلا إذا كان هذا الطلب هو الأحدث لنفس الفلتر
        if (totalsRequestId === totalsRequestIdRef.current) {
          setWarehouseTotals(totalsResponse.warehouses);
          setGrandTotal(totalsResponse.grandTotal);
          console.log('✅ تم تحميل الإجماليات:', totalsResponse);
        } else {
          console.log('⏭️ تجاهل إجماليات قديمة (طلب أحدث قيد التنفيذ)');
        }
      } catch (totalsError) {
        console.error('❌ خطأ في تحميل الإجماليات:', totalsError);
        // لا نعرض خطأ للمستخدم - الإجماليات اختيارية
      } finally {
        if (totalsRequestId === totalsRequestIdRef.current) {
          setLoadingTotals(false);
        }
      }

    } catch (error) {
      console.error('خطأ في تحميل بيانات المخزون:', error);
      console.log('يحتاج إنشاء /api/inventory-report في الخادم');
      setError('حدث خطأ أثناء تحميل بيانات المخزون. يحتاج إنشاء API في الخادم.');
      setLoading(false);
    }
  };

  // دالة التصفية - إعادة تحميل البيانات من السيرفر مع الفلاتر
  const handleFilter = useCallback((filters: FilterData) => {
    setFilterValues(filters);
    setPage(0); // العودة للصفحة الأولى عند الفلترة
    // سيتم تحميل البيانات تلقائياً عبر useEffect
  }, []);

  // دالة تصدير Excel - جلب جميع البيانات
  const handleExportExcel = useCallback(async () => {
    if (!hasInventoryExportPermission) {
      setError('ليس لديك صلاحية لتصدير التقارير');
      return;
    }

    try {
      setLoading(true);
      
      // جلب جميع البيانات بدون pagination مع تطبيق نفس الفلاتر الحالية
      const exportFilters: any = {
        page: 1,
        limit: 999999, // جلب جميع البيانات
        sortBy: 'productName',
        sortOrder: 'asc'
      };
      if (filterValues.barcode) exportFilters.search = filterValues.barcode;
      if (filterValues.productName) exportFilters.search = filterValues.productName;
      if (filterValues.category) exportFilters.category = filterValues.category;
      if (filterValues.warehouse) exportFilters.warehouse = filterValues.warehouse;

      const response = await inventoryReportService.getInventoryReport(exportFilters);

      if (!response.data || !Array.isArray(response.data)) {
        setError('فشل جلب البيانات للتصدير');
        setLoading(false);
        return;
      }

      // معالجة البيانات للتصدير
      const exportData = response.data.map(reportItem => {
        const row: any = {
          'الباركود': reportItem.barcode || 'غير متوفر',
          'اسم المنتج': reportItem.productName || reportItem.product?.name || 'غير محدد',
          'المتغير': Object.values(reportItem.variantAttributes || {}).join(' - ') || 'غير محدد',
          'التصنيف': reportItem.categories && reportItem.categories.length > 0
            ? reportItem.categories.map(cat => cat.name).join(', ')
            : 'غير مصنف'
        };

        // إضافة أعمدة المستودعات
        warehouses.forEach(warehouse => {
          const whInventory = reportItem.warehouseInventories?.find(
            wh => wh.warehouse?.name === warehouse || wh.warehouseName === warehouse
          );
          row[warehouse] = whInventory?.quantity || 0;
        });

        // حساب المجموع (فقط من المستودعات المعروضة)
        const total = warehouses.reduce((sum, warehouse) => {
          const whInventory = reportItem.warehouseInventories?.find(
            wh => wh.warehouse?.name === warehouse || wh.warehouseName === warehouse
          );
          return sum + (whInventory?.quantity || 0);
        }, 0);
        row['المجموع'] = total;

        return row;
      });

      // إنشاء ورقة العمل
      const ws = XLSX.utils.json_to_sheet(exportData);
      
      // تطبيق تنسيق RTL
      if (!ws['!cols']) ws['!cols'] = [];
      const colCount = Object.keys(exportData[0] || {}).length;
      for (let i = 0; i < colCount; i++) {
        ws['!cols'][i] = { wch: 15 };
      }

      // إنشاء المصنف
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'تقرير المخزون');

      // تصدير الملف
      const fileName = `تقرير_المخزون_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);

      console.log('تم تصدير تقرير المخزون بنجاح - عدد الصفوف:', exportData.length);
      setLoading(false);
    } catch (error) {
      console.error('خطأ في تصدير Excel:', error);
      setError('حدث خطأ أثناء تصدير البيانات');
      setLoading(false);
    }
  }, [warehouses, hasInventoryExportPermission, filterValues]);

  // دالة تغيير الصفحة
  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // دالة تغيير عدد الصفوف في الصفحة
  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // عرض رسالة الخطأ إذا لم تكن هناك صلاحيات
  if (!hasInventoryReportPermission) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          <AlertTitle>غير مصرح</AlertTitle>
          ليس لديك صلاحية للوصول إلى هذه الصفحة
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* العنوان والأزرار */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          تقرير المخزون
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportExcel}
            disabled={!hasInventoryExportPermission || loading}
          >
            تصدير Excel
          </Button>
        </Box>
      </Box>

      {/* رسالة الخطأ */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* مرشح البيانات */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <InventoryReportFilter
          filterValues={filterValues}
          categories={categoriesList}
          warehouses={warehouses}
          onFilterChange={handleFilter}
        />
      </Paper>

      {/* جدول البيانات */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <InventoryReportTable
            data={inventoryData}
            warehouses={warehouses}
            totalItems={totalItems}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            warehouseTotals={warehouseTotals}
            grandTotal={grandTotal}
            loadingTotals={loadingTotals}
          />
        </>
      )}
    </Box>
  );
};

export default InventoryReport;